<script lang="ts">
  import {
    type Cell,
    type Column,
    type ColumnDef as ColDef,
    type ColumnDef,
    type ColumnPinningState,
    FlexRender as flexRender,
    type Header,
    type Row as TRow,
    type TableOptions,
  } from '@tanstack/vue-table';

  import type { StashCommonColor } from '../../../types/colors';
  import Table, { type TableProps } from '../Table/Table.vue';

  export interface DataTableProps<TData = unknown>
    extends Omit<TableProps, 'isSchemaDriven' | 'isSelectable' | 'layout'> {
    /** TanStack column definitions. */
    columns: ColumnDef<TData, any>[];

    /** Row data for the TanStack table instance. */
    data: TData[];

    /**
     * TanStack initialState. Use `initialState.columnPinning` to pin columns left/right.
     * Shape: `{ columnPinning?: { left?: string[]; right?: string[] } }`.
     * When any column is pinned left, `select` and `expand` columns (if enabled) are automatically pinned left.
     */
    initialState?: TableOptions<TData>['initialState'];

    /**
     * When true, a checkbox selection column is prepended and the table uses TanStack row selection (current page only).
     */
    isSelectable?: boolean;

    /**
     * Returns a stable id for each row. When not provided, row index is used.
     */
    getRowId?: (row: TData) => string;

    /**
     * Returns whether the row can be selected. When not provided, all rows are selectable.
     */
    getRowCanSelect?: (row: import('@tanstack/vue-table').Row<TData>) => boolean;

    /**
     * Returns an accent color for the given row. When provided, the result is passed to TableRow for the left-edge accent bar.
     */
    getRowAccentColor?: (row: TData) => StashCommonColor | undefined;

    /**
     * Returns child rows for hierarchical/sub-row expansion.
     */
    getSubRows?: (row: TData) => TData[] | undefined;

    /**
     * Controls which rows can be expanded. Defaults to TanStack's built-in check (has sub-rows).
     * Return `true` for every row to allow custom expansion content via the `#expansion` slot.
     */
    getRowCanExpand?: (row: TRow<TData>) => boolean;

    /**
     * When true, hides the divider between each row and its expanded content.
     */
    hideExpansionDivider?: boolean;
  }
</script>

<script setup lang="ts" generic="TData = unknown">
  import { computed, h, useCssModule } from 'vue';

  import useDataTable from '../../composables/useDataTable/useDataTable';
  import { t } from '../../locale';
  import Checkbox from '../Checkbox/Checkbox.vue';
  import ChevronToggle from '../ChevronToggle/ChevronToggle.vue';
  import Paginate from '../Paginate/Paginate.vue';
  import TableCell from '../TableCell/TableCell.vue';
  import TableHeaderCell from '../TableHeaderCell/TableHeaderCell.vue';
  import TableHeaderRow from '../TableHeaderRow/TableHeaderRow.vue';
  import TableRow from '../TableRow/TableRow.vue';

  defineOptions({ inheritAttrs: false });

  const props = withDefaults(defineProps<DataTableProps<TData>>(), {
    initialState: undefined,
    isSelectable: false,
    getRowId: undefined,
    getRowCanSelect: undefined,
    getRowAccentColor: undefined,
    getSubRows: undefined,
    getRowCanExpand: undefined,
    density: undefined,
    emptyStateText: '',
    hasActions: false,
    hasCustomExpandToggle: false,
    isEmpty: false,
    isLoading: false,
    isExpandable: false,
    layout: 'scroll',
    radius: 'rounded',
    stickyHeader: undefined,
    hideExpansionDivider: false,
  });

  const classes = useCssModule();
  const slots = defineSlots<{
    empty?(): unknown[];
    expansion?(props: { row: TRow<TData> }): unknown[];
  }>();

  const expandColumnDef = computed<ColDef<TData, unknown>>(() => ({
    id: 'expand',
    size: 48,
    minSize: 48,
    maxSize: 48,
    meta: {
      class: `${classes['row-control-cell']} stash-table-cell__is-expansion-cell px-0!`,
    },
    header: () => null,
    cell: ({ row }: { row: TRow<TData> }) =>
      row.getCanExpand()
        ? h(ChevronToggle, {
            'aria-label': row.getIsExpanded() ? t('ll.table.collapseRow') : t('ll.table.expandRow'),
            direction: row.getIsExpanded() ? 'up' : 'down',
            onClick: () => row.toggleExpanded(),
          })
        : null,
  }));

  const selectionColumnDef = computed<ColDef<TData, unknown>>(() => ({
    id: 'select',
    size: 48,
    minSize: 48,
    maxSize: 48,
    meta: {
      class: `${classes['row-control-cell']} stash-table-cell__is-selection-cell lg:pl-3`,
    },
    header: ({ table }: { table: import('@tanstack/vue-table').Table<TData> }) =>
      h(Checkbox, {
        checked: table.getIsAllPageRowsSelected(),
        indeterminate: table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected(),
        title: t('ll.selectAll'),
        class: classes['row-selection-checkbox'],
        'onUpdate:checked': () => table.toggleAllPageRowsSelected(),
      }),
    cell: ({ row }: { row: TRow<TData> }) =>
      h(Checkbox, {
        checked: row.getIsSelected(),
        disabled: !row.getCanSelect(),
        title: t('ll.select.self'),
        class: classes['row-selection-checkbox'],
        'onUpdate:checked': () => row.toggleSelected(),
      }),
  }));

  const effectiveColumns = computed<ColDef<TData, unknown>[]>(() => {
    const cols = [...props.columns];
    if (props.isExpandable && !props.hasCustomExpandToggle) cols.unshift(expandColumnDef.value);
    if (props.isSelectable) cols.unshift(selectionColumnDef.value);
    return cols;
  });

  const mergedInitialState = computed<TableOptions<TData>['initialState']>(() => {
    const base = props.initialState ?? {};
    const pinning: ColumnPinningState = {
      left: [...(base.columnPinning?.left ?? [])],
      right: [...(base.columnPinning?.right ?? [])],
    };

    if (pinning.left!.length > 0) {
      const hasExpand = props.isExpandable && !props.hasCustomExpandToggle;
      if (hasExpand && !pinning.left!.includes('expand')) pinning.left!.unshift('expand');
      if (props.isSelectable && !pinning.left!.includes('select')) pinning.left!.unshift('select');
    }

    return { ...base, columnPinning: pinning };
  });

  const dataTableResult = useDataTable<TData>({
    columns: effectiveColumns,
    data: computed(() => props.data),
    initialState: mergedInitialState.value,
    getSubRows: props.getSubRows,
    getRowCanExpand: props.getRowCanExpand,
    getRowId: props.getRowId,
    ...(props.isSelectable
      ? {
          enableRowSelection: props.getRowCanSelect ? (row) => props.getRowCanSelect!(row) : undefined,
        }
      : {}),
  });

  const { table } = dataTableResult;

  const pinnedLeftWidth = computed(() => table.getLeftLeafColumns().reduce((sum, col) => sum + col.getSize(), 0));
  const pinnedRightWidth = computed(() => table.getRightLeafColumns().reduce((sum, col) => sum + col.getSize(), 0));
  const tableMinWidth = computed(() => (table.getIsSomeColumnsPinned() ? table.getTotalSize() : undefined));

  const showPagination = computed(() => {
    if (dataTableResult.isServerMode) return false;
    return dataTableResult.table.getPageCount() > 1;
  });

  function getColumnSizeStyle(def: { minSize?: number; maxSize?: number }): Record<string, string> | undefined {
    const minSize = def.minSize;
    const maxSize = def.maxSize;
    const style: Record<string, string> = {};
    if (minSize != null && minSize > 0) style.minWidth = `${minSize}px`;
    if (maxSize != null && maxSize < Number.MAX_SAFE_INTEGER) style.maxWidth = `${maxSize}px`;
    return Object.keys(style).length ? style : undefined;
  }

  function getCommonPinningStyles(column: Column<TData, unknown>): Record<string, string> {
    const isPinned = column.getIsPinned();
    if (!isPinned) return {};

    const style: Record<string, string> = {
      position: 'sticky',
      width: `${column.getSize()}px`,
      zIndex: '1',
    };

    if (isPinned === 'left') {
      style.left = `${column.getStart('left')}px`;
    } else {
      style.right = `${column.getAfter('right')}px`;
    }

    return style;
  }

  function getHeaderColumnStyle(header: Header<TData, unknown>): Record<string, string> | undefined {
    const pinned = {
      ...getCommonPinningStyles(header.column),
      zIndex: header.column.getIsPinned() ? '2' : '1',
    };
    return { ...getColumnSizeStyle(header.column.columnDef), ...pinned };
  }

  function getHeaderColumnClass(header: Header<TData, unknown>): string {
    const meta = header.column.columnDef.meta;
    const base = meta?.headerClass ?? meta?.class ?? '';
    if (header.column.id === 'select') {
      return `stash-table-header-row__selection-cell min-w-12 ${classes['row-control-cell']} ${base}`.trim();
    }
    if (header.column.id === 'expand') {
      return `${classes['row-control-cell']} ${base}`.trim();
    }
    const pin = header.column.getIsPinned();
    const pinnedBorder =
      pin === 'left' && header.column.getIsLastColumn('left')
        ? 'border-r border-ice-100'
        : pin === 'right' && header.column.getIsFirstColumn('right')
          ? 'border-l border-ice-100'
          : '';
    return `${base} ${pinnedBorder}`.trim();
  }

  function getCellColumnStyle(cell: Cell<TData, unknown>): Record<string, string> | undefined {
    return { ...getColumnSizeStyle(cell.column.columnDef), ...getCommonPinningStyles(cell.column) };
  }

  function getCellColumnClass(cell: Cell<TData, unknown>): string {
    const meta = cell.column.columnDef.meta;
    const base = meta?.cellClass ?? meta?.class ?? '';
    const pin = cell.column.getIsPinned();
    const pinnedBorder =
      pin === 'left' && cell.column.getIsLastColumn('left')
        ? 'border-r! border-ice-100!'
        : pin === 'right' && cell.column.getIsFirstColumn('right')
          ? 'border-l! border-ice-100!'
          : '';
    return `${base} ${pinnedBorder}`.trim();
  }

  const selectedRows = computed(() => dataTableResult.table.getSelectedRowModel().rows);
  const selectedItems = computed(() => selectedRows.value.map((r) => r.original));
  const allPageRowsSelected = computed(() => dataTableResult.table.getIsAllPageRowsSelected());
  const somePageRowsSelected = computed(() => dataTableResult.table.getIsSomePageRowsSelected());

  defineExpose({
    table: dataTableResult.table,
    selectedRows,
    selectedItems,
    allPageRowsSelected,
    somePageRowsSelected,
  });
</script>

<template>
  <Table
    v-bind="$attrs"
    is-schema-driven
    layout="scroll"
    :density="props.density"
    :empty-state-text="props.emptyStateText"
    :has-actions="props.hasActions"
    :has-custom-expand-toggle="props.isExpandable || props.hasCustomExpandToggle"
    :is-empty="props.isEmpty"
    :is-loading="props.isLoading"
    :is-expandable="props.isExpandable"
    :is-selectable="props.isSelectable"
    :radius="props.radius"
    :sticky-header="props.stickyHeader"
    :pinned-left-width="pinnedLeftWidth"
    :pinned-right-width="pinnedRightWidth"
    :table-min-width="tableMinWidth"
  >
    <template #head>
      <TableHeaderRow v-for="headerGroup in dataTableResult.table.getHeaderGroups()" :key="headerGroup.id">
        <TableHeaderCell
          v-for="header in headerGroup.headers"
          :key="header.id"
          :schema-driven-header="header"
          :class="getHeaderColumnClass(header)"
          :style="getHeaderColumnStyle(header)"
        >
          <flexRender
            v-if="!header.isPlaceholder"
            :render="header.column.columnDef.header"
            :props="header.getContext()"
          />
        </TableHeaderCell>
      </TableHeaderRow>
    </template>

    <template #body>
      <TableRow
        v-for="row in dataTableResult.table.getRowModel().rows"
        :key="row.id"
        :accent-color="props.getRowAccentColor?.(row.original)"
        :is-expanded="row.getIsExpanded()"
        :hide-expansion-divider="props.hideExpansionDivider"
      >
        <template #default>
          <TableCell
            v-for="cell in row.getVisibleCells()"
            :key="cell.id"
            :is-control="cell.column.id === 'select' || cell.column.id === 'expand'"
            :class="getCellColumnClass(cell)"
            :style="getCellColumnStyle(cell)"
          >
            <flexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
          </TableCell>
        </template>
        <template v-if="slots.expansion" #expansion>
          <slot name="expansion" :row="row" />
        </template>
      </TableRow>
    </template>

    <template v-if="$slots.empty" #empty>
      <slot name="empty" />
    </template>
  </Table>

  <Paginate
    v-if="showPagination"
    class="mt-6"
    :current-page="dataTableResult.table.getState().pagination.pageIndex + 1"
    :list-length="dataTableResult.table.getFilteredRowModel().rows.length"
    :page-size="dataTableResult.table.getState().pagination.pageSize"
    @set-page="(page: number) => dataTableResult.table.setPageIndex(page - 1)"
  />
</template>

<style module>
  @reference "../../../styles/main.css";

  @layer utilities {
    .row-selection-checkbox label {
      padding: 0;
      min-height: --spacing(6);
      vertical-align: middle;
    }

    .row-control-cell {
      border-right: 0;
    }
  }
</style>
